import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, X, Phone, Mail, Search, User } from "lucide-react";

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/admin/customers").then(({ data }) => setCustomers(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = customers.filter((c) =>
    `${c.first_name} ${c.last_name} ${c.email} ${c.phone}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="admin-customers">
      <h1 className="font-display text-4xl font-semibold text-brand-charcoal">Klanten</h1>
      <p className="text-brand-muted mt-1">{customers.length} klant{customers.length === 1 ? "" : "en"}</p>

      <div className="relative mt-6 max-w-sm">
        <Search size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-muted" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Zoek op naam, e-mail of telefoon" className="w-full rounded-full border border-brand-border pl-11 pr-4 py-3 text-sm focus:border-brand-pink focus:outline-none" data-testid="customer-search" />
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-brand-pink" size={28} /></div>
      ) : filtered.length === 0 ? (
        <p className="text-brand-muted mt-8">Geen klanten gevonden.</p>
      ) : (
        <div className="mt-6 overflow-hidden bg-white rounded-2xl border border-brand-border">
          {filtered.map((c) => (
            <button key={c.id} onClick={() => setSelected(c)} data-testid={`customer-row-${c.id}`} className="w-full flex items-center justify-between gap-4 px-4 sm:px-5 py-4 border-b border-brand-border last:border-0 hover:bg-brand-blush transition-colors text-left">
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-10 w-10 rounded-full bg-brand-pink/20 flex items-center justify-center text-brand-pinkhover font-semibold shrink-0">
                  {c.first_name?.[0]}{c.last_name?.[0]}
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-brand-charcoal truncate">{c.first_name} {c.last_name}</p>
                  <p className="text-sm text-brand-muted truncate">{c.email}</p>
                </div>
              </div>
              <div className="text-right shrink-0 hidden sm:block">
                <p className="text-sm font-medium text-brand-charcoal">{c.appointment_count} afspr.</p>
                {c.upcoming_appointment && <p className="text-xs text-brand-pinkhover">Komend: {c.upcoming_appointment}</p>}
              </div>
            </button>
          ))}
        </div>
      )}

      {selected && <CustomerModal id={selected.id} onClose={() => setSelected(null)} />}
    </div>
  );
}

function CustomerModal({ id, onClose }) {
  const [c, setC] = useState(null);
  const [note, setNote] = useState("");

  const load = () => api.get(`/admin/customers/${id}`).then(({ data }) => setC(data)).catch(() => {});
  useEffect(() => { load(); }, [id]);

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await api.post(`/admin/customers/${id}/notes`, { note });
      setNote(""); load();
      toast.success("Notitie toegevoegd");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="customer-modal">
      <div className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {!c ? <div className="flex justify-center py-10"><Loader2 className="animate-spin text-brand-pink" /></div> : (
          <>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-full bg-brand-pink/20 flex items-center justify-center text-brand-pinkhover font-semibold">{c.first_name?.[0]}{c.last_name?.[0]}</div>
                <div>
                  <h2 className="font-display text-2xl font-semibold text-brand-charcoal">{c.first_name} {c.last_name}</h2>
                  <div className="flex flex-col gap-0.5 mt-1 text-sm text-brand-muted">
                    <a href={`mailto:${c.email}`} className="flex items-center gap-1.5"><Mail size={13} /> {c.email}</a>
                    <a href={`tel:${c.phone}`} className="flex items-center gap-1.5"><Phone size={13} /> {c.phone}</a>
                  </div>
                </div>
              </div>
              <button onClick={onClose}><X size={20} className="text-brand-muted" /></button>
            </div>

            <h3 className="eyebrow mb-2 mt-5">Afsprakengeschiedenis</h3>
            <div className="space-y-2">
              {c.appointments.length === 0 && <p className="text-sm text-brand-muted">Nog geen afspraken.</p>}
              {c.appointments.map((a) => (
                <div key={a.id} className="flex items-center justify-between gap-3 bg-brand-neutral rounded-xl px-3 py-2.5 text-sm">
                  <div>
                    <p className="font-medium text-brand-charcoal">{a.service_name}</p>
                    <p className="text-brand-muted capitalize">{a.date_display} · {a.start_time}</p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${a.status === "cancelled" ? "bg-gray-100 text-gray-400" : a.status === "completed" ? "bg-green-100 text-green-700" : "bg-brand-pink/20 text-brand-pinkhover"}`}>
                    {a.status === "cancelled" ? "Geannuleerd" : a.status === "completed" ? "Voltooid" : "Bevestigd"}
                  </span>
                </div>
              ))}
            </div>

            <h3 className="eyebrow mb-2 mt-6">Interne notities</h3>
            <div className="space-y-2 mb-3">
              {(c.internal_notes || []).map((n, i) => (
                <div key={i} className="bg-brand-blush rounded-xl px-3 py-2 text-sm text-brand-charcoal">{n.text}</div>
              ))}
            </div>
            <div className="flex gap-2">
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Nieuwe notitie…" className="flex-1 rounded-xl border border-brand-border px-3 py-2 text-sm focus:border-brand-pink focus:outline-none" data-testid="customer-note-input" />
              <button onClick={addNote} className="bg-brand-charcoal text-white font-medium px-4 py-2 rounded-xl text-sm" data-testid="customer-note-add">Toevoegen</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
